import React, { lazy, Suspense, ComponentType } from "react";

interface LoadingComponentProps {
  message?: string;
}

const LoadingComponent: React.FC<LoadingComponentProps> = ({
  message = "Loading...",
}) => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6d8986] mx-auto mb-4"></div>
      <p className="text-gray-600">{message}</p>
    </div>
  </div>
);

interface LazyLoadOptions {
  fallback?: React.ComponentType<LoadingComponentProps>;
  loadingMessage?: string;
}

// Enhanced import function that handles chunk loading failures
function createRetryableImport<T>(
  importFunc: () => Promise<{ default: T }>,
  retries = 3
): () => Promise<{ default: T }> {
  return async () => {
    for (let i = 0; i < retries; i++) {
      try {
        return await importFunc();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);

        // Check if this is a chunk loading failure
        if (
          errorMessage.includes(
            "Failed to fetch dynamically imported module"
          ) ||
          errorMessage.includes("Importing a module script failed") ||
          errorMessage.includes("Loading chunk") ||
          errorMessage.includes("Loading CSS chunk")
        ) {

          // On the last retry, trigger a hard reload
          if (i === retries - 1) {
            console.error(
              "All chunk loading attempts failed. Reloading page..."
            );
            window.location.reload();
            // Return a never-resolving promise to prevent further execution
            return new Promise(() => {});
          }

          // Wait a bit before retrying
          await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
        } else {
          // For non-chunk errors, throw immediately
          throw error;
        }
      }
    }

    // This should never be reached, but TypeScript needs it
    throw new Error("Unexpected error in chunk loading retry logic");
  };
}

export function lazyLoad<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  options: LazyLoadOptions = {}
): React.ComponentType<React.ComponentProps<T>> {
  const {
    fallback: Fallback = LoadingComponent,
    loadingMessage = "Loading...",
  } = options;

  // Create a retryable version of the import function
  const retryableImport = createRetryableImport(importFunc);
  const LazyComponent = lazy(retryableImport);

  return (props: React.ComponentProps<T>) => (
    <Suspense fallback={<Fallback message={loadingMessage} />}>
      <LazyComponent {...props} />
    </Suspense>
  );
}

// Specialized lazy loaders for different features
export const lazyLoadFeature = (
  importFunc: () => Promise<{ default: ComponentType<any> }>,
  featureName: string
) =>
  lazyLoad(importFunc, {
    loadingMessage: `Loading ${featureName}...`,
  });

export const lazyLoadPage = (
  importFunc: () => Promise<{ default: ComponentType<any> }>,
  pageName: string
) =>
  lazyLoad(importFunc, {
    loadingMessage: `Loading ${pageName} page...`,
  });

export const lazyLoadTab = (
  importFunc: () => Promise<{ default: ComponentType<any> }>,
  tabName: string
) =>
  lazyLoad(importFunc, {
    loadingMessage: `Loading ${tabName} tab...`,
  });
